import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { API, getAuthHeaders } from '../../utils/admin-helpers';

const URL = `${API}/api/admin/do-not-contact`;

export const DoNotContactDialog = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [email, setEmail] = useState('');

  const load = useCallback(() => {
    axios.get(URL, { headers: getAuthHeaders(), params: { search } }).then((r) => setItems(r.data.items)).catch(() => toast.error('Could not load list'));
  }, [search]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(URL, { email, reason: 'Added by admin' }, { headers: getAuthHeaders() });
      setEmail(''); toast.success('Added to Do Not Contact'); load();
    } catch { toast.error('Enter a valid email'); }
  };

  const remove = async (addr) => {
    if (!window.confirm(`Remove ${addr} from Do Not Contact? They could receive marketing again if they have consent.`)) return;
    await axios.delete(`${URL}/${encodeURIComponent(addr)}`, { headers: getAuthHeaders() });
    toast.success('Removed'); load();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="bg-transparent text-white border-white/20" data-testid="dnc-open-btn">Do Not Contact</Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 text-white border-slate-700 max-w-2xl" data-testid="dnc-dialog">
        <DialogHeader><DialogTitle>Do Not Contact list ({items.length})</DialogTitle></DialogHeader>
        <form onSubmit={add} className="flex gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Add email address" className="bg-slate-800 border-slate-700" data-testid="dnc-add-input" />
          <Button type="submit" data-testid="dnc-add-btn">Add</Button>
        </form>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" className="bg-slate-800 border-slate-700" data-testid="dnc-search-input" />
        <div className="max-h-80 overflow-y-auto divide-y divide-slate-800" data-testid="dnc-list">
          {items.length === 0 && <p className="text-sm text-slate-400 py-4">No contacts on the list.</p>}
          {items.map((i) => (
            <div key={i.email} className="flex items-center justify-between py-2 text-sm" data-testid={`dnc-row-${i.email}`}>
              <div><div>{i.email}</div><div className="text-xs text-slate-400">{i.reason} · {i.source} · {i.created_at?.slice(0, 10)}</div></div>
              <Button size="sm" variant="ghost" onClick={() => remove(i.email)} data-testid={`dnc-remove-${i.email}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
